'use client'

import { SearchIcon } from 'lucide-react'
import React, { useEffect, useMemo, useState } from 'react'

import { Button } from '../ui/Button'
import { cn } from '../ui/cn'
import { Input } from '../ui/Input'
import { Wrapper } from '../ui/Wrapper'

import { QuestionItem } from './FAQ/QuestionItem'
import { type FAQItem, faqItems } from './faqItems'

const CopyBlock = Wrapper

interface FAQProps {
  group?: string | string[]
  /** Section heading. Playground calls this `title`; kept as `heading` for
   *  Kettle's existing consumers (FAQ page, HowItWorks page). */
  heading?: React.ReactNode
  description?: React.ReactNode
  searchPlaceholder?: string
  searchLabel?: string
  /** When true, every FAQ item renders expanded on load. Used by the
   *  dedicated FAQ page; other consumers (home, how-it-works) leave items
   *  collapsed so the section stays scannable. */
  defaultItemsOpen?: boolean
}

// Matches playground `FAQ` default — "Frequently Asked Questions" with
// "Questions" in the brand-dark accent.
const DEFAULT_HEADING: React.ReactNode = (
  <>
    Frequently Asked <span className="snplay:text-primaryDark"> Questions</span>
  </>
)
const DEFAULT_DESCRIPTION = 'Browse common queries or use the search bar below.'

const toGroups = (group?: string | string[]): string[] => {
  return group ? (Array.isArray(group) ? group : [group]) : []
}

const normalize = (value: string): string => {
  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim()
}

const getMatchScore = (item: FAQItem, queryTokens: string[]): number => {
  const fields = [
    { text: normalize(item.question), weight: 4 },
    { text: normalize(item.tags.join(' ')), weight: 3 },
    { text: normalize(item.category), weight: 2 },
    { text: normalize(item.answerText), weight: 1 },
  ]

  if (!queryTokens.every((token) => fields.some(({ text }) => text.includes(token)))) {
    return 0
  }

  return queryTokens.reduce(
    (score, token) =>
      score +
      fields.reduce(
        (tokenScore, { text, weight }) => tokenScore + (text.includes(token) ? weight : 0),
        0,
      ),
    0,
  )
}

export const FAQ = ({
  group,
  heading = DEFAULT_HEADING,
  description = DEFAULT_DESCRIPTION,
  searchPlaceholder = 'Search FAQs',
  searchLabel = 'Search frequently asked questions',
  defaultItemsOpen = false,
}: FAQProps): React.ReactElement => {
  const [query, setQuery] = useState('')
  const [activeGroup, setActiveGroup] = useState<string | string[] | undefined>(group)
  const [hashId, setHashId] = useState<string | null>(null)
  const normalizedQuery = normalize(query)
  const activeGroups = useMemo(() => toGroups(activeGroup), [activeGroup])
  const isGrouped = activeGroups.length > 0

  useEffect(() => {
    setActiveGroup(group)
  }, [group])

  useEffect(() => {
    const readHash = (): void => {
      const raw = window.location.hash.replace(/^#/, '')
      setHashId(raw.startsWith('faq-') ? raw.slice(4) : null)
    }
    readHash()
    window.addEventListener('hashchange', readHash)
    return (): void => window.removeEventListener('hashchange', readHash)
  }, [])

  useEffect(() => {
    if (!hashId) return
    const target = document.getElementById(`faq-${hashId}`)
    target?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [hashId])

  const filteredItems = useMemo(() => {
    if (!normalizedQuery) {
      return isGrouped
        ? faqItems.filter((item) =>
            toGroups(item.group).some((itemGroup) => activeGroups.includes(itemGroup)),
          )
        : faqItems
    }

    const queryTokens = normalizedQuery.split(/\s+/)

    return faqItems
      .map((item) => ({ item, score: getMatchScore(item, queryTokens) }))
      .filter(({ score }) => score > 0)
      .sort((a, b) => b.score - a.score)
      .map(({ item }) => item)
  }, [activeGroups, isGrouped, normalizedQuery])

  const resultLabel = `${filteredItems.length} ${filteredItems.length === 1 ? 'FAQ' : 'FAQs'} found`

  return (
    <section
      className={cn(
        'snplay-bounds snplay:grid snplay:grid-cols-1',
        'snplay:gap-8 snplay:my-24 snplay:xs:grid-cols-12',
      )}
    >
      <CopyBlock
        className={cn(
          'snplay:xs:sticky snplay:xs:top-30 snplay:xs:col-span-5',
          'snplay:xs:mt-20 snplay:xs:self-start snplay:lg:col-span-4',
        )}
      >
        <h1 className="snplay:text-4xl snplay:font-bold">{heading}</h1>
        <p className="snplay:mt-4 snplay:text-lg snplay:text-ui-secondary">{description}</p>
        <div className="snplay:mt-6">
          <label htmlFor="faq-search" className="snplay:sr-only">
            {searchLabel}
          </label>
          <Input
            id="faq-search"
            type="search"
            value={query}
            onChange={(event): void => setQuery(event.target.value)}
            placeholder={searchPlaceholder}
            autoComplete="off"
            aria-controls="faq-results"
            showResetHelper={Boolean(query)}
            leadingIcon={
              <SearchIcon aria-hidden="true" className="snplay:size-5 snplay:text-ui-secondary" />
            }
          />
          <p
            aria-live="polite"
            aria-atomic="true"
            className="snplay:mt-2 snplay:text-sm snplay:text-ui-secondary"
          >
            {normalizedQuery ? resultLabel : ' '}
          </p>
        </div>
      </CopyBlock>
      <div
        className={cn(
          'snplay:col-span-1 snplay:xs:col-span-7',
          'snplay:lg:col-span-6 snplay:lg:col-start-7',
        )}
      >
        <ul id="faq-results" className="snplay:space-y-2">
          {filteredItems.map((item) => (
            <QuestionItem
              key={item.id}
              id={item.id}
              question={item.question}
              answer={item.answer}
              group={item.group}
              defaultOpen={defaultItemsOpen || hashId === item.id}
            />
          ))}
        </ul>
        {filteredItems.length === 0 && (
          <div
            className={cn(
              'snplay:rounded-lg snplay:border snplay:p-6',
              'snplay:border-[rgb(var(--snplay-ui-decorator-medium-rgb))]',
            )}
          >
            <p className="snplay:font-bold snplay:text-ui-primary">No FAQs found</p>
            <p className="snplay:mt-2 snplay:text-base snplay:text-ui-secondary">
              Try a different word or clear your search to browse all FAQs.
            </p>
            <Button variant="default" className="snplay:mt-4" onClick={(): void => setQuery('')}>
              Clear search
            </Button>
          </div>
        )}
        {filteredItems.length > 0 && !normalizedQuery && (
          <Button
            variant="primary"
            size="large"
            className="snplay:mt-8"
            onClick={(): void => setActiveGroup(isGrouped ? undefined : 'default')}
          >
            {isGrouped ? 'View More FAQs' : 'View Less'}
          </Button>
        )}
      </div>
    </section>
  )
}
